import { useCallback, useEffect, useReducer, useState } from 'react'
import { useIsMounted } from '@dtx-company/true-common/src/hooks/useIsMounted'

export interface DimensionObject {
  topIsNegative: boolean
  left: number
  right: number
  width: number
  height: number
}

export type UseDimensionsHook = [
  setRef: (node: HTMLElement | null) => void,
  dimensions: DimensionObject,
  ref: HTMLElement | null,
  remeasure: () => void
]

export interface UseDimensionsArgs {
  liveMeasure?: boolean
}

function getDimensionObject(node: HTMLElement | null): DimensionObject {
  const rect = node?.getBoundingClientRect()

  return {
    topIsNegative: (rect?.top ?? 0) <= 0,
    left: rect?.left || 0,
    width: rect?.width || 0,
    height: rect?.height || 0,
    right: rect?.right || 0
  }
}

export function dimensionReducer(state: DimensionObject, s: DimensionObject): DimensionObject {
  if (
    state.topIsNegative !== s.topIsNegative ||
    state.width !== s.width ||
    state.height !== s.height ||
    state.left !== s.left ||
    state.right !== s.right
  ) {
    return s
  }
  return state
}

export function useDimensions({ liveMeasure = true }: UseDimensionsArgs = {}): UseDimensionsHook {
  const isMounted = useIsMounted()
  const [dimensions, setDimensions] = useReducer(dimensionReducer, {
    topIsNegative: false,
    width: 0,
    height: 0,
    left: 0,
    right: 0
  })
  const [node, setNode] = useState<HTMLElement | null>(null)

  const ref = useCallback((node: HTMLElement | null) => {
    setNode(node)
  }, [])

  useEffect(() => {
    if (node) {
      const measure = (): number | undefined =>
        window.requestAnimationFrame(() => {
          if (isMounted()) setDimensions(getDimensionObject(node))
        })
      measure()

      if (liveMeasure) {
        window.addEventListener('resize', measure)
        window.addEventListener('scroll', measure)

        return () => {
          window.removeEventListener('resize', measure)
          window.removeEventListener('scroll', measure)
        }
      }
    }
  }, [node, liveMeasure, isMounted])

  const remeasure = useCallback(
    () => window.requestAnimationFrame(() => setDimensions(getDimensionObject(node))),
    [node]
  )

  return [ref, dimensions, node, remeasure]
}
